// ForgotPasswordComponent.js
import React, { useEffect, useRef } from "react";
import { useAtom } from "jotai";

import { useForgotPassword, useI18N } from "common/hooks";
import Loader from "@/components/common/Loader";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/shadcn/components/ui/alert";
import { emailAtom } from "@/store/atoms";
import { useDarkMode } from "@/hooks/useDarkMode";
import AnalyticsService from "@/services/analytics";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";
import FloatingLabelInput from "@/components/InputBoxes/TextInput";
import WideButton from "@/components/common/WideButton";
import { ArrowLeft } from "lucide-react";

interface EnterPasswordProps {
  onClose: () => void;
  title: string;
}

const ForgotPasswordComponent = ({ onClose, title }: EnterPasswordProps) => {
  const i18n = useI18N();
  const isDark = useDarkMode();
  const [email] = useAtom(emailAtom);

  const { forgotPassword, error, isLoading, result } = useForgotPassword();

  const { setScreenIndex } = useAuthModals();

  const handleContinue = async () => {
    forgotPassword({ email });
  };

  useEffect(() => {
    AnalyticsService.trackGAEventHandler("forgotPasswordOpen");
    AnalyticsService.trackMx("forgotPasswordOpen", {});
  }, []);

  useEffect(() => {
    if (result?.status) {
      AnalyticsService.trackGAEventHandler("forgotPasswordSubmit");
      AnalyticsService.trackMx("forgotPasswordSubmit", {});

      console.log("forgotPassword done");
    }
  }, [result, setScreenIndex, onClose, email]);

  useEffect(() => {
    if (error) {
      AnalyticsService.trackGAEventHandler("forgotPasswordErrorSubmit");
      AnalyticsService.trackMx("forgotPasswordErrorSubmit", {});
    }
  }, [error]);

  const backToPassword = async () => {
    AnalyticsService.trackGAEventHandler("forgotPasswordClose");
    AnalyticsService.trackMx("forgotPasswordClose", {});

    //move to enter password
    setScreenIndex(AuthModalScreen.EnterPassword);
  };

  const firstFocusRef = useRef(null); // Reference for the element we need to set focus

  // Focus on the element when the component mounts
  useEffect(() => {
    if (firstFocusRef.current) {
      firstFocusRef.current.focus();
    }
  }, []);

  return (
    <section
      style={{
        height:
          window.innerHeight < 800
            ? `${window.innerHeight - 40}px`
            : `${window.innerHeight - 100}px`,
      }}
      className="relative flex flex-col bg-white dark:bg-primary-900 rounded-xl overflow-y-auto"
    >
      <header className="py-4 min-h-14 flex items-center justify-center text-center px-6 border-b border-primary-200 dark:border-primary-150 sticky top-0">
        <h1
          className="desktop-h1-xxsmall-bold text-center text-primary-800 dark:text-primary-150"
          tabIndex={0}
          ref={firstFocusRef} // Attach the ref here
        >
          {title}
        </h1>
      </header>

      <div className="px-8 md:px-16 flex-1 flex flex-col">
        <div className="flex justify-center my-6">
          <img
            src="/images/Group.svg"
            alt=""
            className="w-[100px] md:w-[120px]"
            aria-hidden="true"
          />
        </div>
        <h2 className="desktop-h2-small-bold" tabIndex={0}>
          It's okay! We can help!
        </h2>
        <div
          className="paragraph-medium-medium mt-4 text-primary-800 dark:text-primary-150"
          tabIndex={0}
        >
          Enter your email address below and we’ll get you and email to reset
          your password.
        </div>
        <div className="my-4 border-b border-b-primary-150 w-full"></div>
        <div className="mt-1">
          <FloatingLabelInput
            readonly
            label="Email"
            required
            type="email"
            value={email}
            placeholder="Email"
            onChange={() => {}}
            errorMessage={
              error?.message
                ? "You've tried to use forgot password too many times. Please try again in 5 minutes."
                : undefined
            }
          />
        </div>
        <div className="mt-2">
          <span
            className="paragraph-small-medium text-primary-800 dark:text-primary-150"
            tabIndex={0}
          >
            Already know your details? You can{" "}
          </span>
          <a
            className=" paragraph-small-medium underline text-[#352FC3] dark:text-[#A39FE8]  mr-1 cursor-pointer"
            tabIndex={0}
            onClick={() => {
              setScreenIndex(AuthModalScreen.EnterEmail);
            }}
          >
            Log in here
          </a>
        </div>
        <div className="flex-1">
          {isLoading && <Loader />}

          {result?.status && (
            <Alert className="mb-6">
              <AlertTitle></AlertTitle>
              <AlertDescription>{result?.data.message}</AlertDescription>
            </Alert>
          )}
        </div>
        <div className="mb-16">
          <WideButton
            type="filled"
            disabled={email.length <= 0}
            onClick={handleContinue}
          >
            {i18n.get("continue")}
          </WideButton>
        </div>
      </div>

      <button onClick={backToPassword} className="absolute top-4 left-4">
        <ArrowLeft color={isDark ? "#A39FE8" : "#352FC3"} />
      </button>
    </section>
  );
};

export default ForgotPasswordComponent;
